import { jsx as _jsx } from "react/jsx-runtime";
/**
 * External dependencies
 */
import { createContext, useReducer } from 'react';
import { getArrayFromStorage, saveArrayToStorage } from '../utils';
/**
 * Constants
 */
const initialState = {
    accessibility: [],
    dates: [],
    categories: [],
    keyword: '',
    filtering: false,
    filtersLength: 0,
    filteredLength: 0,
    filteredPosts: [],
    order: 'title',
    posts: [],
    postTypes: [],
    regions: [],
    questions: [],
    answers: {},
    search: '',
    showMap: false,
    visible: 12,
    wishlist: getArrayFromStorage('scrimWishlist') || [],
};
export const ADD_TO_POST_TYPES = 'ADD_TO_POST_TYPES';
export const ADD_TO_VISIBLE = 'ADD_TO_VISIBLE';
export const ADD_TO_WISHLIST = 'ADD_TO_WISHLIST';
export const SET_ACCESSIBILITY = 'SET_ACCESSIBILITY';
export const SET_ANSWERS = 'SET_ANSWERS';
export const SET_CATEGORIES = 'SET_CATEGORIES';
export const SET_DATES = 'SET_DATES';
export const SET_FILTERING = 'SET_FILTERING';
export const SET_FILTERED_POSTS = 'SET_FILTERED_POSTS';
export const SET_KEYWORD = 'SET_KEYWORD';
export const SET_ORDER = 'SET_ORDER';
export const SET_POSTS = 'SET_POSTS';
export const SET_POST_TYPES = 'SET_POST_TYPES';
export const SET_QUESTIONS = 'SET_QUESTIONS';
export const SET_REGIONS = 'SET_REGIONS';
export const SET_VISIBLE = 'SET_VISIBLE';
export const TOGGLE_MAP_VIEW = 'TOGGLE_MAP_VIEW';
export const REMOVE_FROM_POST_TYPES = 'REMOVE_FROM_POST_TYPES';
export const REMOVE_FROM_WISHLIST = 'REMOVE_FROM_WISHLIST';
const store = createContext({
    state: initialState,
    dispatch: (action) => console.error('Dispatched action outside of an store provider', action)
});
const { Provider } = store;
const StateProvider = ({ children }) => {
    const [state, dispatch] = useReducer((state, action) => {
        switch (action.type) {
            case ADD_TO_VISIBLE:
                return {
                    ...state,
                    visible: state.visible + 12
                };
            case ADD_TO_WISHLIST:
                if (!state.wishlist.includes(action.id)) {
                    saveArrayToStorage('scrimWishlist', [...state.wishlist, action.id]);
                }
                return {
                    ...state,
                    wishlist: state.wishlist.includes(action.id) ? [...state.wishlist] : [...state.wishlist, action.id]
                };
            case ADD_TO_POST_TYPES:
                return {
                    ...state,
                    postTypes: state.postTypes.includes(action.postType) ? [...state.postTypes] : [...state.postTypes, action.postType]
                };
            case REMOVE_FROM_POST_TYPES:
                return {
                    ...state,
                    postTypes: state.postTypes.filter((postType) => action.postType !== postType)
                };
            case REMOVE_FROM_WISHLIST:
                const wishlist = state.wishlist.filter((item) => action.id !== item);
                saveArrayToStorage('scrimWishlist', wishlist);
                return {
                    ...state,
                    wishlist
                };
            case SET_ACCESSIBILITY:
                return {
                    ...state,
                    accessibility: [...action.accessibility]
                };
            case SET_ANSWERS:
                return {
                    ...state,
                    answers: action.answers
                };
            case SET_CATEGORIES:
                return {
                    ...state,
                    categories: [...action.categories]
                };
            case SET_DATES:
                return {
                    ...state,
                    dates: [...action.dates]
                };
            case SET_FILTERING:
                return {
                    ...state,
                    filtering: action.filtering
                };
            case SET_FILTERED_POSTS:
                const { accessibility, categories, dates, keyword, order, regions, postTypes } = state;
                const filterArray = (arr1, arr2) => {
                    return arr1.some(item => arr2.includes(item));
                };
                const filterByValue = (string, obj) => {
                    let hasValue = false;
                    const haystack = Object.values(obj).toString().toLowerCase().replaceAll(' & ', ' and ');
                    const needle = string.toLowerCase().replaceAll(' & ', ' and ');
                    const needles = needle.split(' ');
                    for (const needle of needles) {
                        hasValue = haystack.includes(needle);
                        if (hasValue) {
                            break;
                        }
                    }
                    return hasValue;
                };
                const posts = state.posts?.filter((post) => {
                    let hasKeyword = false;
                    let inAccessibility = false;
                    let inDates = false;
                    let inCategories = false;
                    let inRegions = false;
                    let inPostTypes = false;
                    if (post.accessibility && accessibility.length > 0)
                        inAccessibility = filterArray(accessibility, post.accessibility);
                    if (post.categories && categories.length > 0)
                        inCategories = filterArray(categories, post.categories);
                    if (post.dates && dates.length > 0)
                        inDates = filterArray(dates, post.dates);
                    if (post.postType && postTypes.length > 0)
                        inPostTypes = postTypes.includes(post.postType);
                    if (post.region && regions.length > 0)
                        inRegions = regions.includes(post.region);
                    if (keyword.length > 3)
                        hasKeyword = filterByValue(keyword.trim(), post);
                    if (accessibility.length === 0 && categories.length === 0 && dates.length === 0 && postTypes.length === 0 && regions.length === 0 && keyword.length < 3) {
                        return true;
                    }
                    if (post.dates && dates.length > 0 && post.categories && categories.length > 0) {
                        return filterArray(categories, post.categories) && filterArray(dates, post.dates);
                    }
                    return inAccessibility || inCategories || inRegions || hasKeyword || inPostTypes || inDates;
                });
                const sortArray = (arr) => {
                    if (order === 'date') {
                        return arr.sort((a, b) => (new Date(b.date).valueOf() - new Date(a.date).valueOf()));
                    }
                    return arr.sort((a, b) => {
                        const x = a.title.toUpperCase(), y = b.title.toUpperCase();
                        return x == y ? 0 : x > y ? 1 : -1;
                    });
                };
                return {
                    ...state,
                    filtersLength: accessibility.length + categories.length + dates.length + regions.length + postTypes.length,
                    filteredPosts: sortArray(posts),
                    visible: 12
                };
            case SET_KEYWORD:
                return {
                    ...state,
                    keyword: action.keyword
                };
            case SET_ORDER:
                return {
                    ...state,
                    order: action.order
                };
            case SET_POSTS:
                return {
                    ...state,
                    posts: action.posts
                };
            case SET_POST_TYPES:
                return {
                    ...state,
                    postTypes: [...action.postTypes]
                };
            case SET_QUESTIONS:
                return {
                    ...state,
                    questions: action.questions
                };
            case SET_REGIONS:
                return {
                    ...state,
                    regions: [...action.regions]
                };
            case SET_VISIBLE:
                return {
                    ...state,
                    visible: action.visible
                };
            case TOGGLE_MAP_VIEW:
                return {
                    ...state,
                    showMap: action.showMap
                };
            default:
                throw new Error();
        }
    }, initialState);
    return _jsx(Provider, { value: { state, dispatch }, children: children });
};
export { store, StateProvider };
